import { Clock } from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import React, { useEffect, useState } from "react";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Driver, Orders } from "@/interface/entityInterface";
import { ordersTableCol } from "@/components/common/columns/columns";
import { addMonths } from "date-fns";
import { useNavigate } from "react-router-dom";
import { useGetList } from "react-admin";
import { useUserContext } from "@/context/UserContext";
import AssignDriver from "@/components/common/orders/AssignDriver";
import axiosInstance from "@/utils/axiosInstance";
import { Badge } from "@/components/ui/badge";

const UpcomingOrders = ({ rowClick }: { rowClick: (row: Orders) => void }) => {
  const { decodedToken } = useUserContext();
  const navigate = useNavigate();
  const statusItems = [
    { label: "Today", status: "TODAY" },
    { label: "Next 3 days", status: "NEXT_3_DAYS" },
    { label: "Next 7 days", status: "NEXT_7_DAYS" },
  ];
  const [drivers, setDrivers] = useState<Driver[]>([]);

  const [currStatus, setCurrStatus] = useState("TODAY");

  const { data: upcomingOrders = [] } = useGetList(`taxi-orders/upcoming`, {
    pagination: { page: 1, perPage: 5 },
    filter: {
      currStatus,
    },
  });

  useEffect(() => {
    const unsub = async () => {
      const { data } = await axiosInstance.get("/drivers");
      setDrivers(data);
    };

    unsub();
  });

  const columns: ColumnDef<Orders>[] = ordersTableCol({
    columns:
      decodedToken?.permissions?.user_role === "owner"
        ? [
            {
              accessorKey: "accept_reject",
              header: "Status",
              cell: ({ row }: any) => (
                <Badge variant="outline">
                  <span className="text-gray-500 text-xs">
                    {row.original.status}
                  </span>
                </Badge>
              ),
            },
            {
              accessorKey: "assign_drivers",
              header: "Driver",
              cell: ({ row }: any) => (
                <AssignDriver order={row.original} drivers={drivers} />
              ),
            },
          ]
        : [],
  });

  const table = useReactTable({
    data: upcomingOrders,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    manualPagination: true,
  });

  const handleDetailClick = (e: any, row: Orders) => {
    if (e.target && e.target.role === "checkbox") {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    rowClick(row);
  };

  return (
    <div className="border rounded-lg shadow-md p-5">
      <div className="flex items-center space-x-2 mb-3">
        <Clock className="text-gray-500" />
        <h2 className="text-lg font-semibold">Order manifest</h2>
      </div>

      <ScrollArea className="mb-4">
        <Tabs value={currStatus}>
          <TabsList>
            {statusItems.map((item) => (
              <TabsTrigger
                key={item.status}
                value={item.status}
                onClick={() => setCurrStatus(item.status)}
              >
                {item.label}
              </TabsTrigger>
            ))}
            <TabsTrigger
              value="All"
              onClick={() =>
                navigate("/orders", {
                  state: { endDate: addMonths(new Date(), 1) },
                })
              }
            >
              Next 30 days
            </TabsTrigger>
          </TabsList>
        </Tabs>
        <ScrollBar orientation="horizontal" />
      </ScrollArea>

      <div className="w-full rounded-md border overflow-auto">
        <Table>
          <TableHeader className="bg-muted sticky top-0 z-10">
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead key={header.id}>
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>

          <TableBody>
            {upcomingOrders.length > 0 ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && "selected"}
                  className="cursor-pointer"
                  onClick={(e: React.MouseEvent) =>
                    handleDetailClick(e, row.original)
                  }
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center"
                >
                  No results.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

export default UpcomingOrders;
